/**
 * @flow
 */

import React, { useState } from 'react';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSelector } from 'react-redux';
import { FormattedMessage, injectIntl, defineMessages } from 'react-intl';
import { Button, Icon } from 'semantic-ui-react';
import { format } from 'date-fns';
import withModal from '../../../../lib/withModal';
import ArchiveDialog from './ArchiveDialog';
import { GET_SALON_QUERY } from '../../containers/timetable/MasterTable';
import { GET_MASTER_APPOINTMENTS_QUERY } from '../../../master-appointments/containers/queries';

const intlMessages = defineMessages({
    defaultErrorMessage: {
        id: 'errors.defaultMessage',
        defaultMessage: 'Something went wrong',
    },
});

export const ARCHIVE_APPOINTMENT_MUTATION = gql`
    mutation archiveAppointment($input: ArchiveAppointmentInput!) {
        archiveAppointment(input: $input) {
            appointment {
                id
                archived
            }
        }
    }
`;

const updateAppointmentInSalon = (
    store,
    salon,
    appointment,
    showArchive,
    currentLanguage,
) => {
    try {
        const variables = {
            salon,
            showArchive,
            date: format(new Date(appointment.startAt), 'yyyy-MM-dd'),
            lang: currentLanguage,
        };
        const data = store.readQuery({
            query: GET_SALON_QUERY,
            variables,
        });
        const masters = data.viewer.salon.masters.nodes || [];
        const nextMasters = masters.map(m => {
            if (m.id !== appointment.master.id) {
                return m;
            }
            return {
                ...m,
                appointments: (m.appointments || []).filter(
                    item => item.id !== appointment.id,
                ),
            };
        });
        store.writeQuery({
            query: GET_SALON_QUERY,
            variables,
            data: {
                ...data,
                viewer: {
                    ...data.viewer,
                    salon: {
                        ...data.viewer.salon,
                        masters: {
                            ...data.viewer.salon.masters,
                            nodes: nextMasters,
                        },
                    },
                },
            },
        });
    } catch (e) {
        // Cache miss — safe to ignore
    }
};

const updateMasterAppointment = (
    store,
    master,
    salon,
    appointment,
    fromDate,
    toDate,
    showArchive,
    currentLanguage,
) => {
    try {
        const variables = {
            master,
            salon,
            fromDate: format(new Date(fromDate), 'yyyy-MM-dd'),
            toDate: format(new Date(toDate), 'yyyy-MM-dd'),
            withMaster: true,
            showArchive,
            lang: currentLanguage,
        };
        const data = store.readQuery({
            query: GET_MASTER_APPOINTMENTS_QUERY,
            variables,
        });
        const appointments = data.viewer.salon.master.appointments || [];
        store.writeQuery({
            query: GET_MASTER_APPOINTMENTS_QUERY,
            variables,
            data: {
                ...data,
                viewer: {
                    ...data.viewer,
                    salon: {
                        ...data.viewer.salon,
                        master: {
                            ...data.viewer.salon.master,
                            appointments: appointments.filter(
                                item => item.id !== appointment.id,
                            ),
                        },
                    },
                },
            },
        });
    } catch (e) {
        // Cache miss — safe to ignore
    }
};

const renderLabel = isArchived => {
    if (isArchived) {
        return (
            <FormattedMessage
                id="pages.appointments.details.ArchiveButton.unarchive"
                defaultMessage="Unarchive"
            />
        );
    }
    return (
        <FormattedMessage
            id="pages.appointments.details.ArchiveButton.archive"
            defaultMessage="Archive"
        />
    );
};

const ArchiveButton = ({
    appointment,
    open,
    onOpen,
    onClose,
    archiveAppointment: archiveAppointmentProp,
    masterAppointments,
    fromDate,
    toDate,
    intl,
}) => {
    const salon = useSelector(({ user }) => user.get('salon'));
    const showArchive = useSelector(({ salon: salonState }) =>
        salonState.get('showArchive'),
    );
    const currentLanguage = useSelector(
        ({ intl: intlState }) =>
            intlState.get('locale') || intlState.get('defaultLanguage'),
    );

    const [archiveMutation, { loading }] = useMutation(
        ARCHIVE_APPOINTMENT_MUTATION,
    );
    const [archiveError, setArchiveError] = useState(null);

    const onArchive = () => {
        setArchiveError(null);
        const mutation = archiveMutation({
            variables: {
                input: {
                    salon,
                    appointmentId: appointment.id,
                    archived: !appointment.archived,
                },
            },
            update: store => {
                if (!showArchive) {
                    if (masterAppointments) {
                        updateMasterAppointment(
                            store,
                            appointment.master.id,
                            salon,
                            appointment,
                            fromDate,
                            toDate,
                            showArchive,
                            currentLanguage,
                        );
                    } else {
                        if (archiveAppointmentProp) {
                            archiveAppointmentProp(appointment);
                        } else {
                            updateAppointmentInSalon(
                                store,
                                salon,
                                appointment,
                                showArchive,
                                currentLanguage,
                            );
                        }
                    }
                }
            },
        });

        return mutation
            .then(() => {
                onClose();
            })
            .catch(() => {
                // Surface the failure in the dialog so the user can retry
                // (the old redux-form flow threw SubmissionError for this)
                setArchiveError(
                    intl.formatMessage(intlMessages.defaultErrorMessage),
                );
            });
    };

    return (
        <ArchiveDialog
            trigger={
                <Button onClick={onOpen} color="orange" floated="right">
                    <Icon name="archive" />
                    {renderLabel(appointment.archived)}
                </Button>
            }
            appointment={appointment}
            open={open}
            onClose={onClose}
            onArchive={onArchive}
            submitting={loading}
            error={archiveError}
        />
    );
};

export default injectIntl(withModal(ArchiveButton));
